import Chip from '@material-ui/core/Chip'
import classNames from 'classnames'
import { DEFAULTS } from './constants'
import Button from '../Button'
import RrIcon from '../RrIcon'
import { nextStep, MATCHING_COLUMNS } from './helpers'
import { startCase } from 'Shared/functions/util'

function filterColumns(uploadType, matchMode, columns, useIdentifierGroup) {
  if (uploadType === 'upload-list') {
    return columns.filter((column) => column.required)
  }
  if (matchMode === 'new') {
    const cols = columns.filter((column) => column.requiredForCreation || column.name === 'Membership Number')
    return useIdentifierGroup ? cols.filter((col) => !MATCHING_COLUMNS.map(c => c.label).includes(col.name)) : cols
  }
  return useIdentifierGroup ? [] : columns.filter((column) => column.name === 'Membership Number')
}

const ColumnCheck = ({
  show,
  type,
  step,
  paragraph,
  columns,
  onComplete,
  confirmation = true,
  labels,
  confirmedColumns,
  setConfirmedColumns,
  onUploadTypeChange,
  allowNonExternalIdMatching,
  steps,
}) => {
  const [confirmed, setConfirmed] = useState(!confirmation)
  const optionalColumns = _.sortBy(columns.filter((column) => !column.required), ['name'])
  const [uploadType, setUploadType] = useState('')

  useEffect(() => {
    onUploadTypeChange?.(uploadType)
  }, [uploadType])

  const useIdentifierGroup = !!(allowNonExternalIdMatching && type === 'upload-people' && uploadType !== '')
  const filteredColumns = filterColumns(type, uploadType, columns, useIdentifierGroup)
  const identifierOptions = useIdentifierGroup ? columns.filter((col) => MATCHING_COLUMNS.map(c => c.label).includes(col.name)) : []
  const shouldShowOptionalColumns = type !== 'upload-list' && uploadType !== ''
  const showColumnCheck = type === 'upload-list' || uploadType !== ''

  const isConfirmedCheck = (newConfirmed) => {
    if (useIdentifierGroup) {
      const standardDone = filteredColumns
        .filter((col) => !!col.required)
        .every((col) => newConfirmed.includes(col.name))
      const identifierDone = identifierOptions.some((col) => newConfirmed.includes(col.name))
      return standardDone && identifierDone
    }
    return filteredColumns
      .filter((column) => !!column.required)
      .every((column) => newConfirmed.includes(column.name))
  }

  const onClickColumn = (column) => {
    if (type === 'upload-list') {
      setConfirmedColumns([column])
      setConfirmed(true)
    } else {
      const newConfirmed = confirmedColumns.concat(column)
      setConfirmedColumns(newConfirmed)
      setConfirmed(isConfirmedCheck(newConfirmed))
    }
  }

  const renderChip = (column) => {
    const clicked = confirmedColumns.includes(column.name)
    return (
      <Chip
        className={classNames('chip', 'required', { confirmed: clicked })}
        clickable={!clicked}
        key={column.name}
        label={startCase(column.name)}
        size="small"
        deleteIcon={
          clicked ? (
            <RrIcon iconName="done" classes="chip-icon --checked" />
          ) : (
            <RrIcon iconName="done" classes="chip-icon --unchecked" />
          )
        }
        variant={!clicked ? 'outlined' : 'default'}
        onDelete={() => onClickColumn(column.name)}
        onClick={() => onClickColumn(column.name)}
      />
    )
  }

  if (!show) {
    return null
  }

  return (
    <div>
      <p>{paragraph || DEFAULTS.paragraphs.check}</p>
      {confirmation && <p>{DEFAULTS.paragraphs.checkWithConfirm}</p>}

      {type !== 'upload-list' && (
        <div>
          <h3 className="margin-bottom">Are you uploading new people, or just updating existing ones?</h3>
          <label className="margin-bottom">
            <input type="radio" checked={uploadType === 'new'} onChange={() => setUploadType('new')} />
            Uploading new people, and updating existing people
          </label>
          <label>
            <input type="radio" checked={uploadType === 'update'} onChange={() => setUploadType('update')} />
            Just updating existing people
          </label>
        </div>
      )}

      {showColumnCheck && (
        <div className="cc-chiplist margin-top double">
          {/* upload-list: single required column picker */}
          {type === 'upload-list' && (
            <>
              <h3>{labels.requiredColumns}</h3>
              {labels.requiredColumnsDescriptionLists.map((p, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
              ))}
              <div className="chips">{filteredColumns.map(renderChip)}</div>
            </>
          )}

          {/* upload-people without setting: existing required column chips */}
          {type !== 'upload-list' && !useIdentifierGroup && (
            <>
              <h3>{labels.requiredColumns}</h3>
              {labels.requiredColumnsDescription.map((p, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
              ))}
              <div className="chips">{filteredColumns.map(renderChip)}</div>
            </>
          )}

          {/* upload-people with setting: standard required + identifier group */}
          {useIdentifierGroup && (
            <>
              {filteredColumns.length > 0 && (
                <>
                  <h3>{labels.requiredColumns}</h3>
                  {labels.requiredColumnsDescription.map((p, i) => (
                    <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
                  ))}
                  <div className="chips">{filteredColumns.map(renderChip)}</div>
                </>
              )}
              <h3 className={filteredColumns.length > 0 ? 'margin-top double' : ''}>
                Identifier <span className="remove-bold smaller dark-grey">(at least one required)</span>
              </h3>
              <p>
                Your CSV must include at least one of these columns to identify people.
                Click on one to confirm.
              </p>
              <div className="chips">{identifierOptions.map(renderChip)}</div>
            </>
          )}
        </div>
      )}

      {shouldShowOptionalColumns && (
        <div className="cc-chiplist margin-top double">
          <h3>{labels.optionalColumns}</h3>

          {labels.optionalColumnsDescription.map((p, i) => (
            <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
          ))}

          <div className="chips" style={{ maxHeight: '100px', overflowY: 'scroll' }}>
            {optionalColumns.map((column) => (
              <Chip
                className="chip optional"
                key={column.name}
                label={startCase(column.name)}
                size="small"
              />
            ))}
          </div>
        </div>
      )}

      <Button
        onClick={onComplete}
        disabled={!confirmed}
        extraClass={'no-margin-left margin-top double'}
        primary
      >{nextStep(step, steps)}</Button>
    </div>
  )
}

export default ColumnCheck
